<template>
  <div style="padding-top: 3em;">
    <h4 class="title">Insurance Form</h4>
    <p>
      Before completing this form please ensure that you have read the information on our
      <a
        target="_blank"
        href
      >web page</a>.
    </p>
    <p>You will be required to submit all of the details relating to your request in one visit and the form will not save the information you have entered if you leave the page or close your session, so please make sure you have all the relevant information and attachments prepared prior to starting your request.</p>
    <p>
      <br>If you have any concerns or difficulties in completing the form please contact our Customer Service team on 08 8405 6600.
    </p>
    <p>
      <br>Once you submission has been completed you will receive an automatically generated e-mail with a copy of the information you have submitted and a unique reference number. Once received Council will process the request and provide you with a response detailing the next steps of the process.
    </p>
    <br>

    <div v-if="message" class="notification is-primary">{{ message }}</div>

    <form-wizard
      @submit.prevent="onSubmit"
      @on-complete="onSubmit"
      @on-change="testChange"
      shape="tab"
      color="#17C4EE"
      title
      subtitle
      ref="wizard"
      v-if="!submitted"
    >
      <input type="hidden" name="_token" :value="csrf">
      <input type="hidden" v-model="form">

      <div style="padding: 1em 0px; color: #aaa;">
        <small>* Required Fields</small>
      </div>

      <!-- ************************* -->
      <!-- TAB #1 - CLAIMANT DETAILS -->
      <!-- ************************* -->
      <tab-content title="Claimant Details" :before-change="validateStep1">
        <div class="columns is-multiline">
          <div class="column is-4">
            <div class="field">
              <label class="label">First Name *</label>
              <div class="control">
                <input
                  type="text"
                  autofocus
                  placeholder="Please enter your first name"
                  :class="{ 'input': true, 'is-danger': $v.claimantFirstName.$error }"
                  @input="$v.claimantFirstName.$touch()"
                  id="claimantFirstName"
                  name="claimantFirstName"
                  v-model="claimantFirstName"
                >
                <span
                  v-if="$v.claimantFirstName.$error && !$v.claimantFirstName.required"
                  class="help is-danger"
                >First name is required</span>
                <span
                  v-if="$v.claimantFirstName.$error && !$v.claimantFirstName.AlphNumCharsValidator"
                  class="help is-danger"
                >Invalid characters entered</span>
              </div>
            </div>
          </div>

          <div class="column is-4">
            <div class="field">
              <label class="label">Last Name *</label>
              <div class="control">
                <input
                  type="text"
                  placeholder="Please enter your last name"
                  :class="{ 'input': true, 'is-danger': $v.claimantLastName.$error }"
                  @input="$v.claimantLastName.$touch()"
                  name="claimantLastName"
                  v-model="claimantLastName"
                >
                <span
                  v-if="$v.claimantLastName.$error && !$v.claimantLastName.required"
                  class="help is-danger"
                >Last name is required</span>
                <span
                  v-if="$v.claimantLastName.$error && !$v.claimantLastName.AlphNumCharsValidator"
                  class="help is-danger"
                >Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-multiline">
          <div class="column is-4">
            <div class="field">
              <label class="label">Email Address *</label>
              <div class="control">
                <input
                  type="text"
                  placeholder="Please enter your email address"
                  :class="{ 'input': true, 'is-danger': $v.claimantEmailAddress.$error }"
                  @input="$v.claimantEmailAddress.$touch()"
                  name="claimantEmailAddress"
                  v-model="claimantEmailAddress"
                >
                <span
                  v-if="$v.claimantEmailAddress.$error && !$v.claimantEmailAddress.required"
                  class="help is-danger"
                >Email Address is required</span>
                <span
                  v-if="$v.claimantEmailAddress.$error && !$v.claimantEmailAddress.email"
                  class="help is-danger"
                >Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-multiline">
          <div class="column is-4">
            <div class="field">
              <label class="label">Contact Number *</label>
              <div class="control">
                <input
                  type="text"
                  placeholder="Please enter your first name"
                  :class="{ 'input': true, 'is-danger': $v.claimantContactNumber.$error }"
                  @input="$v.claimantContactNumber.$touch()"
                  id="claimantContactNumber"
                  name="claimantContactNumber"
                  v-model="claimantContactNumber"
                >
                <span
                  v-if="$v.claimantContactNumber.$error && !$v.claimantContactNumber.required"
                  class="help is-danger"
                >Contact number is required</span>
                <span
                  v-if="$v.claimantContactNumber.$error && !$v.claimantContactNumber.numeric"
                  class="help is-danger"
                >Invalid characters entered</span>
              </div>
            </div>
          </div>
        </div>

        <div class="box" style="background: #fefefe; margin-top: .5em;">
          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="form-check">
                <label class="form-check-label">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    value="Yes"
                    :checked="claimantResident"
                    @click="claimantResident = !claimantResident"
                  >&nbsp;
                  <strong>Are you a resident of the City of Port Adelaide Enfield</strong>
                </label>
              </div>
            </div>
          </div>

          <div class="columns is-multiline" v-if="claimantResident">
            <div class="field column is-7">
              <label
                class="label"
              >Select residential address * (Within the City of Port Adelaide Enfield)</label>
              <div class="control">
                <PropertyAddress
                  fieldName="claimantAddressPAE"
                  source="/api/address/search"
                  placeholder="eg. 1/10 Main St Semaphore"
                  v-on:address="saveClaimantAddress"
                  v-on:addressTyping="saveClaimantAddressTyping"
                  v-on:addressPropertyNo="saveClaimantPropertyNo"
                ></PropertyAddress>
                <span
                  v-if="$v.claimantAddressTyping.$error && !$v.claimantAddressTyping.required"
                  class="help is-danger"
                >Address is required</span>
              </div>
            </div>
          </div>

          <div class="columns is-multiline" v-if="!claimantResident">
            <div class="field column is-7">
              <label
                class="label"
              >Select residential address * (Outside of the City of Port Adelaide Enfield)</label>
              <div class="control">
                <GoogleAddressAutocomplete
                  fieldName="claimantAddressOther"
                  placeholder="eg. 1/10 Main St Semaphore"
                  v-on:address="saveClaimantAddress"
                  v-on:addressTyping="saveClaimantAddressTyping"
                  v-on:addressPropertyNo="saveClaimantPropertyNo"
                ></GoogleAddressAutocomplete>
                <span
                  v-if="$v.claimantAddressTyping.$error && !$v.claimantAddressTyping.required"
                  class="help is-danger"
                >Address is required</span>
              </div>
            </div>
          </div>
        </div>
      </tab-content>

      <!-- ****************************** -->
      <!-- TAB #2 - ADDITIONAL DETAILS -->
      <!-- ****************************** -->
      <tab-content title="Additional Details" :before-change="validateStep2">
        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  type="checkbox"
                  autofocus
                  class="form-check-input"
                  value="Yes"
                  :checked="vehicleClaim"
                  @click="vehicleClaim = !vehicleClaim"
                >&nbsp;
                <strong>Damage to a motor vehicle?</strong>
              </label>
            </div>
          </div>
        </div>

        <div class="box" style="background: #fefefe; margin-top: .5em;" v-if="vehicleClaim">
          <div class="columns is-multiline">
            <div class="column is-4">
              <div class="field">
                <label class="label">Registration Number *</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the registration number"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.vehicleRegistrationNumber.$error }"
                    @input="$v.vehicleRegistrationNumber.$touch()"
                    v-model="vehicleRegistrationNumber"
                  >
                  <span
                    v-if="$v.vehicleRegistrationNumber.$error && !$v.vehicleRegistrationNumber.required"
                    class="help is-danger"
                  >Registration number is required</span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-multiline">
            <div class="column is-4">
              <div class="field">
                <label class="label">Make *</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the make"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.vehicleMake.$error }"
                    @input="$v.vehicleMake.$touch()"
                    v-model="vehicleMake"
                  >
                  <span
                    v-if="$v.vehicleMake.$error && !$v.vehicleMake.required"
                    class="help is-danger"
                  >Vehicle make is required</span>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-multiline">
            <div class="column is-4">
              <div class="field">
                <label class="label">Model *</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the model"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.vehicleModel.$error }"
                    @input="$v.vehicleModel.$touch()"
                    v-model="vehicleModel"
                  >
                  <span
                    v-if="$v.vehicleModel.$error && !$v.vehicleModel.required"
                    class="help is-danger"
                  >Vehicle model is required</span>
                </div>
              </div>
            </div>
          </div>
          <br>
          <div class="columns is-multiline" style="background: #eee;">
            <div class="column is-4">
              <div class="field">
                <label class="label">Insurer</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the name of the insurer"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.vehicleInsurer.$error }"
                    @input="$v.vehicleInsurer.$touch()"
                    v-model="vehicleInsurer"
                  >
                  <span
                    v-if="$v.vehicleInsurer.$error && !$v.vehicleInsurer.required"
                    class="help is-danger"
                  >Vehicle insurer is required</span>
                </div>
              </div>
            </div>
            <div class="column is-4">
              <div class="field">
                <label class="label">Policy Number</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the policy number"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.vehiclePolicyNumber.$error }"
                    @input="$v.vehiclePolicyNumber.$touch()"
                    v-model="vehiclePolicyNumber"
                  >
                  <span
                    v-if="$v.vehiclePolicyNumber.$error && !$v.vehiclePolicyNumber.required"
                    class="help is-danger"
                  >Vehicle policy number is required</span>
                </div>
              </div>
            </div>
            <div class="column is-4">
              <div class="field">
                <label class="label">Claim Number</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the policy number"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.vehicleClaimNumber.$error }"
                    @input="$v.vehicleClaimNumber.$touch()"
                    v-model="vehicleClaimNumber"
                  >
                  <span
                    v-if="$v.vehicleClaimNumber.$error && !$v.vehicleClaimNumber.required"
                    class="help is-danger"
                  >Vehicle claim number is required</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  type="checkbox"
                  class="form-check-input"
                  value="Yes"
                  :checked="propertyClaim"
                  @click="propertyClaim = !propertyClaim"
                >&nbsp;
                <strong>Damage to property?</strong>
              </label>
            </div>
          </div>
        </div>

        <div class="box" style="background: #fefefe; margin-top: .5em;" v-if="propertyClaim">
          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">Damaged Property Description *</label>
                <div class="control">
                  <textarea
                    class="textarea"
                    v-model="propertyDamageDetails"
                    placeholder="Please enter details of damage..."
                  ></textarea>
                </div>
                <span
                  v-if="$v.propertyDamageDetails.$error"
                  class="help is-danger"
                >Description is required</span>
              </div>
            </div>
          </div>

          <div class="columns is-multiline" style="background: #eee;">
            <div class="column is-4">
              <div class="field">
                <label class="label">Insurer</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the name of the insurer"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.propertyInsurer.$error }"
                    @input="$v.propertyInsurer.$touch()"
                    v-model="propertyInsurer"
                  >
                  <span
                    v-if="$v.propertyInsurer.$error && !$v.propertyInsurer.required"
                    class="help is-danger"
                  >Property insurer is required</span>
                </div>
              </div>
            </div>
            <div class="column is-4">
              <div class="field">
                <label class="label">Policy Number</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the policy number"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.propertyPolicyNumber.$error }"
                    @input="$v.propertyPolicyNumber.$touch()"
                    v-model="propertyPolicyNumber"
                  >
                  <span
                    v-if="$v.propertyPolicyNumber.$error && !$v.propertyPolicyNumber.required"
                    class="help is-danger"
                  >Property policy number is required</span>
                </div>
              </div>
            </div>
            <div class="column is-4">
              <div class="field">
                <label class="label">Claim Number</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the policy number"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.propertyClaimNumber.$error }"
                    @input="$v.propertyClaimNumber.$touch()"
                    v-model="propertyClaimNumber"
                  >
                  <span
                    v-if="$v.propertyClaimNumber.$error && !$v.propertyClaimNumber.required"
                    class="help is-danger"
                  >Property claim number is required</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  type="checkbox"
                  class="form-check-input"
                  value="Yes"
                  :checked="personalClaim"
                  @click="personalClaim = !personalClaim"
                >&nbsp;
                <strong>A personal injury?</strong>
              </label>
            </div>
          </div>
        </div>

        <div class="box" style="background: #fefefe; margin-top: .5em;" v-if="personalClaim">
          <div class="columns is-multiline">
            <div class="field column is-7">
              <label class="label">Person Injured *</label>
              <div class="control">
                <input
                  type="text"
                  class="input"
                  style="background: #fefefe;"
                  placeholder="Please enter name of person injured"
                  v-model="personalFullName"
                >
                <span v-if="!$v.personalFullName.required" class="help is-danger">Name is required</span>
              </div>
            </div>
          </div>

          <div class="columns is-multiline">
            <div class="column is-4">
              <div class="field">
                <label class="label">
                  Date of Birth
                  <small class="has-text-weight-light">(Click on icon to select date)</small> *
                </label>
                <div class="control">
                  <input
                    type="text"
                    class="input"
                    style="width: 7em; float: left; display: inline;"
                    :value="customFormatter(personalDOBPicker)"
                    disabled
                  >
                  <datepicker
                    :initialView="'year'"
                    :format="customFormatter"
                    id="datepicker2"
                    :disabled-dates="disabled"
                    :input-class="'input ' + classPersonalDOB"
                    @input="checkPersonalDOBValue"
                    name="personalDOBPicker"
                    :placeholder="'dd/mm/yyyy'"
                    v-model="personalDOBPicker"
                  ></datepicker>
                </div>
              </div>
            </div>
          </div>
        </div>
      </tab-content>

      <!-- ****************************** -->
      <!-- TAB #3 - INCIDENT DETAILS -->
      <!-- ****************************** -->
      <tab-content title="Incident Details" :before-change="validateStep3">
        <div class="columns is-multiline">
          <div class="column is-4">
            <div class="field">
              <label class="label">
                Date/Time of incident
                <small
                  class="has-text-weight-light"
                >(Click on icon to select date)</small> *
              </label>
              <div class="control">
                <input
                  type="text"
                  :class="'input ' + classIncidentDate"
                  style="width: 7em; float: left; display: inline;"
                  :value="customFormatter(incidentDatePicker)"
                  disabled
                >
                <datepicker
                  :format="customFormatter"
                  id="datepicker"
                  :disabled-dates="disabled"
                  :input-class="'input'"
                  @input="checkIncidentDateValue"
                  name="incidentDatePicker"
                  :placeholder="'dd/mm/yyyy'"
                  v-model="incidentDatePicker"
                ></datepicker>
                <input
                  ref="timeField"
                  type="text"
                  placeholder="hh:mm"
                  :class="{ 'input': true, 'is-danger': $v.incidentTime.$error }"
                  @input="$v.incidentTime.$touch()"
                  id="incidentTime"
                  name="incidentTime"
                  v-model="incidentTime"
                >
                <span class="select" style="vertical-align: middle;">
                  <select v-model="incidentTimeAMPM">
                    <option>AM</option>
                    <option>PM</option>
                  </select>
                </span>
                <span
                  v-if="$v.incidentTime.$error && !$v.incidentTime.required"
                  class="help is-danger"
                  style="display: inline;"
                >Time is required</span>
                <span
                  v-if="$v.incidentTime.$error && !$v.incidentTime.TimeValidator"
                  class="help is-danger"
                  style="display: inline;"
                >Invalid Time</span>
              </div>
              <div
                style="margin: .6em 0; font-size: .95em;"
              >{{ formatDateTime(incidentDatePicker, incidentTime + ' ' + incidentTimeAMPM) }}</div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="field">
              <label class="label">Incident Address?</label>

              <div class="box" style="background: #fefefe; margin-top: .5em;">
                <div class="columns is-multiline">
                  <div class="column is-12">
                    <div class="form-check">
                      <label class="form-check-label">
                        <strong>Did the incident happen at the following address?</strong>
                        ({{ claimantPropertyNo > 0 ? claimantAddress : claimantAddressTyping }})
                        <div class="control">
                          <label class="radio">
                            <input type="radio" name="sameAddress" v-model="sameAddress" value="1">
                            Yes
                          </label>
                          <label class="radio">
                            <input type="radio" name="sameAddress" v-model="sameAddress" value="0">
                            No
                          </label>
                          <template v-if="sameAddress == 2">
                            <span
                              v-if="$v.incidentAddress.$error"
                              class="help is-danger"
                            >Please select an option.</span>
                          </template>
                        </div>
                      </label>
                    </div>
                  </div>

                  <template v-if="sameAddress==0">
                    <div class="column is-12">
                      <hr style="margin: 0px; margin-bottom: .5em;">
                      <div class="form-check">
                        <strong>Is the address within the City of Port Adelaide Enfield area?</strong>
                        <div class="control">
                          <label class="radio">
                            <input type="radio" name="incidentPAE" v-model="incidentPAE" value="1">
                            Yes
                          </label>
                          <label class="radio">
                            <input type="radio" name="incidentPAE" v-model="incidentPAE" value="0">
                            No
                          </label>

                          <template v-if="incidentPAE == 2">
                            <span
                              v-if="$v.incidentAddress.$error"
                              class="help is-danger"
                            >Please select an option.</span>
                          </template>
                        </div>
                      </div>
                    </div>

                    <div class="field column is-12 test">
                      <template v-if="incidentPAE <= 1">
                        <template v-if="incidentPAE == 1">
                          <label class="label">
                            Incident Address
                            <small
                              class="has-text-weight-light"
                            >(Within the City of Port Adelaide Enfield)</small> *
                          </label>
                          <div class="control">
                            <PropertyAddress
                              fieldName="incidentAddress"
                              source="/api/address/search"
                              placeholder="eg. 1/10 Main St Semaphore"
                              v-on:address="saveIncidentAddress"
                              v-on:addressPropertyNo="saveIncidentPropertyNo"
                            ></PropertyAddress>
                            <span
                              v-if="$v.incidentAddress.$error"
                              class="help is-danger"
                            >Valid address is required.</span>
                          </div>
                        </template>

                        <template v-if="incidentPAE == 0">
                          <label class="label">
                            Incident Address
                            <small
                              class="has-text-weight-light"
                            >(Outside of the City of Port Adelaide Enfield)</small> *
                          </label>
                          <div class="control">
                            <GoogleAddressAutocomplete
                              fieldName="incidentAddressOther"
                              placeholder="eg. 1/10 Main St Semaphore"
                              v-on:address="saveIncidentAddress"
                              v-on:addressPropertyNo="saveIncidentPropertyNo"
                            ></GoogleAddressAutocomplete>
                            <span
                              v-if="$v.incidentAddress.$error"
                              class="help is-danger"
                            >Valid address is required.</span>
                          </div>
                        </template>
                      </template>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="field">
              <label class="label">How did the incident happen? *</label>
              <div class="control">
                <textarea
                  class="textarea"
                  v-model="incidentHow"
                  placeholder="Please describe how the incident happened..."
                ></textarea>
              </div>
              <span
                v-if="$v.incidentHow.$error"
                class="help is-danger"
              >Description of how the incident happened is required.</span>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="field">
              <label class="label">Provide details of the loss, damage or injury suffered *</label>
              <div class="control">
                <textarea
                  class="textarea"
                  v-model="incidentDamageDetail"
                  placeholder="Detail the loss, damage or injury"
                ></textarea>
              </div>
              <span
                v-if="$v.incidentDamageDetail.$error"
                class="help is-danger"
              >Details of injury/damage is required.</span>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <label class="label">Please provide a copy of any relevant documentation or photographs</label>
            <FileDragDropInstant v-on:fileIDs="appendFileIDs" v-on:removeFileID="removeFileID"></FileDragDropInstant>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  type="checkbox"
                  class="form-check-input"
                  value="Yes"
                  :checked="incidentWitness"
                  @click="incidentWitness = !incidentWitness"
                >&nbsp;
                <strong>Were there any witnesses to the incident?</strong>
              </label>
            </div>
            <div v-if="incidentWitness" class="box" style="background: #fefefe; margin-top: .5em;">
              <div class="columns is-multiline" style="padding-bottom: 0px;">
                <div class="field column is-7" style="padding-bottom: 0px;">
                  <label class="label">Name *</label>
                </div>
                <div class="field column is-5" style="padding-bottom: 0px;">
                  <label class="label">Contact Number *</label>
                </div>
              </div>

              <div
                class="columns is-multiline"
                style="padding-bottom: 0px;"
                v-for="(witness, index) in witnesses"
              >
                <div class="field column is-7" style="padding-top: 0px; padding-bottom: 0px;">
                  <!-- <label class="label">Name *</label> -->
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      style="background: #fefefe;"
                      placeholder="Please enter witness name"
                      v-model="witness.name"
                    >
                    <span
                      v-if="!$v.witnesses.$each[index].name.required"
                      class="help is-danger"
                    >Witness name is required</span>
                  </div>
                </div>
                <div class="field column is-5" style="padding-top: 0px; padding-bottom: 0px;">
                  <!-- <label class="label">Contact Number *</label> -->
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      style="background: #fefefe;"
                      placeholder="Please enter contact number"
                      v-model="witness.contactNumber"
                    >
                    <span
                      v-if="!$v.witnesses.$each[index].contactNumber.required"
                      class="help is-danger"
                    >Witness contact number is required</span>
                  </div>
                </div>
              </div>
              <div class="columns is-multiline">
                <div class="field column is-12">
                  <button class="button" @click="witnesses.push({name: ''})">Add Another Witness</button>
                  <button class="button" @click="witnesses.pop()" v-if="witnesses.length > 1">Remove</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  type="checkbox"
                  class="form-check-input"
                  value="Yes"
                  :checked="incidentPoliceInformed"
                  @click="incidentPoliceInformed = !incidentPoliceInformed"
                >&nbsp;
                <strong>Have the Police been informed?</strong>
              </label>
            </div>
            <div
              class="box"
              style="background: #fefefe; margin-top: .5em;"
              v-if="incidentPoliceInformed"
            >
              <div class="field">
                <label class="label">Police Report Number *</label>
                <div class="control">
                  <input
                    type="text"
                    placeholder="Please enter the Police Report Number"
                    style="background: #fefefe;"
                    :class="{ 'input': true, 'is-danger': $v.incidentPoliceReportNumber.$error }"
                    @input="$v.incidentPoliceReportNumber.$touch()"
                    v-model="incidentPoliceReportNumber"
                  >
                  <span
                    v-if="$v.incidentPoliceReportNumber.$error && !$v.incidentPoliceReportNumber.required"
                    class="help is-danger"
                  >Police Report Number is required</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="field">
              <label class="label">City of Port Adelaide Enfield Reference Number</label>
              <div class="control">
                <input
                  :class="{ 'input': true, 'is-danger': $v.paeReferenceNumber.$error }"
                  v-model="paeReferenceNumber"
                  placeholder="Reference Number"
                >
              </div>
              <small>Only required if you have logged a customer request with Council.</small>
              <span v-if="$v.paeReferenceNumber.$error" class="help is-danger">Invalid Format.</span>
            </div>
          </div>
        </div>

        <div class="columns is-multiline">
          <div class="column is-8">
            <div class="field">
              <label class="label">Additional Comments</label>
              <div class="control">
                <textarea
                  class="textarea"
                  v-model="incidentComments"
                  placeholder="Please enter further comments..."
                ></textarea>
              </div>
              <span v-if="$v.incidentComments.$error" class="help is-danger">Invalid Format.</span>
            </div>
          </div>
        </div>
      </tab-content>

      <div class="column is-8" v-if="enableGoogleCaptch">
        <div class="field">
          <vue-recaptcha
            ref="invisibleRecaptcha"
            @verify="onVerify"
            @expired="onExpired"
            size="invisible"
            :sitekey="googleCaptchaKey"
          ></vue-recaptcha>
        </div>
      </div>

      <div class="modal" v-bind:class="{'is-active' : loading}">
        <div class="modal-background"></div>
        <div class="modal-content has-text-centered">
          <h1 class="title is-3 has-text-light">Please wait...</h1>
          <button
            class="button is-info is-loading is-fullwidth is-large"
            style="font-size: 5em; background: transparent;"
          >Submitting</button>
        </div>
      </div>

      <hr>
    </form-wizard>
  </div>
</template>

<script>
const isDate = value => moment(value, "DD/MM/YYYY", true).isValid();

import VueRecaptcha from "vue-recaptcha";

import PropertyAddress from "../../../../../../resources/assets/js/components/address/Typeahead-CouncilAddress.vue";
import GoogleAddressAutocomplete from "../../../../../../resources/assets/js/components/address/GoogleAddressAutocomplete.vue";

import FileDragDropInstant from "../../../../../../Modules/FileManagement/Resources/assets/js/components/FileDragDropInstant.vue";

import {
  alpha,
  required,
  requiredIf,
  email,
  numeric,
  alphaNum,
  minValue
} from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import moment from "moment";

// Custom Validators
import { TimeValidator } from "../../../../../../resources/assets/js/validators/time-validator";
import { AlphNumCharsValidator } from "../../../../../../resources/assets/js/validators/alpha-num-chars-validator";

import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";

import datepicker from "vuejs-datepicker";

export default {
  components: {
    PropertyAddress,
    GoogleAddressAutocomplete,
    FileDragDropInstant,
    VueRecaptcha,
    FormWizard,
    TabContent,
    datepicker
  },

  props: {
    form: {}
  },

  data() {
    return {
      googleCaptchaKey: "6LcyrE0UAAAAAExxoj3NBm1vA-WMu88CIigyzkBU",
      enableGoogleCaptch: false,

      csrf: "",
      formTitle: this.form,
      message: "",
      submitted: false,
      loading: false,

      fileIDs: [],

      // FORM DATA
      // ***********

      format: "dd/MM/yyyy",
      disabled: {
        from: new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() + 1
        )
      },

      // -- Claimant Details
      claimantFirstName: "",
      claimantLastName: "",
      claimantEmailAddress: "",
      claimantContactNumber: "",
      claimantResident: true,
      claimantAddress: "",
      claimantAddressTyping: "",
      claimantPropertyNo: "",

      // -- Vehicle Details
      vehicleClaim: false,
      vehicleRegistrationNumber: "",
      vehicleMake: "",
      vehicleModel: "",
      vehicleInsurer: "",
      vehiclePolicyNumber: "",
      vehicleClaimNumber: "",

      // -- Property Details
      propertyClaim: false,
      propertyDamageDetails: "",
      propertyInsurer: "",
      propertyPolicyNumber: "",
      propertyClaimNumber: "",

      // -- Personal Details
      personalClaim: false,
      personalFullName: "",
      personalDOB: "",
      personalDOBPicker: new Date(moment()),
      classPersonalDOB: "",

      // -- Incident Details
      incidentDate: "",
      incidentDatePicker: new Date(moment()),
      classIncidentDate: "",
      incidentTime: "00:00",
      incidentTimeAMPM: "AM",
      sameAddress: 2,
      incidentPAE: 2,
      incidentAddress: "",
      incidentPropertyNo: "",
      incidentHow: "",
      incidentDamageDetail: "",
      incidentWitness: false,
      witnesses: [
        {
          name: "",
          contactNumber: ""
        }
      ],
      incidentPoliceInformed: false,
      incidentPoliceReportNumber: "",
      paeReferenceNumber: "",
      incidentComments: "",

      agree: false
    };
  },

  validations: {
    claimantFirstName: { required, AlphNumCharsValidator },
    claimantLastName: { required, AlphNumCharsValidator },
    claimantEmailAddress: { required, email },
    claimantContactNumber: { required },
    claimantResident: true,
    claimantAddressTyping: { required },

    formStep1: [
      "claimantFirstName",
      "claimantLastName",
      "claimantEmailAddress",
      "claimantContactNumber",
      "claimantAddressTyping"
    ],

    vehicleRegistrationNumber: {
      required: requiredIf(function() {
        return this.vehicleClaim;
      })
    },
    vehicleMake: {
      required: requiredIf(function() {
        return this.vehicleClaim;
      })
    },
    vehicleModel: {
      required: requiredIf(function() {
        return this.vehicleClaim;
      })
    },
    vehicleInsurer: {},
    vehiclePolicyNumber: {},
    vehicleClaimNumber: {},

    propertyDamageDetails: {
      required: requiredIf(function() {
        return this.propertyClaim;
      })
    },
    propertyInsurer: {},
    propertyPolicyNumber: {},
    propertyClaimNumber: {},

    personalFullName: {
      required: requiredIf(function() {
        return this.personalClaim;
      })
    },
    personalDOB: {
      required: requiredIf(function() {
        return this.personalClaim;
      })
    },

    formStep2: [
      "vehicleRegistrationNumber",
      "vehicleMake",
      "vehicleModel",
      "propertyDamageDetails",
      "personalFullName"
    ],

    incidentDate: { required },
    incidentTime: { required, TimeValidator },
    incidentAddress: { required },
    incidentHow: { required },
    incidentDamageDetail: { required },
    witnesses: {
      required,
      $each: {
        name: {
          required: requiredIf(function() {
            return this.incidentWitness;
          })
        },
        contactNumber: {
          required: requiredIf(function() {
            return this.incidentWitness;
          })
        }
      }
    },
    incidentPoliceReportNumber: {
      required: requiredIf(function() {
        return this.incidentPoliceInformed;
      })
    },
    paeReferenceNumber: {},
    incidentComments: {},

    formStep3: [
      "incidentDate",
      "incidentTime",
      "incidentAddress",
      "incidentHow",
      "incidentDamageDetail",
      "witnesses",
      "incidentPoliceReportNumber"
    ]
  },

  watch: {
    message: function(val) {
      if (!val === "") {
        this.loading = false;
      }
    },
    incidentDatePicker: function() {
      this.incidentDate = this.saveDateTime(
        this.incidentDatePicker,
        this.incidentTime + " " + this.incidentTimeAMPM
      );
    },
    incidentTime: function() {
      this.incidentDate = this.saveDateTime(
        this.incidentDatePicker,
        this.incidentTime + " " + this.incidentTimeAMPM
      );
      this.updateAMPM();
    },
    incidentTimeAMPM: function() {
      this.incidentDate = this.saveDateTime(
        this.incidentDatePicker,
        this.incidentTime + " " + this.incidentTimeAMPM
      );
      this.updateAMPM();
    },
    sameAddress: function(val) {
      if (val == 1) {
        this.sameAddress = 1;
        this.incidentAddress =
          this.claimantPropertyNo > 0
            ? this.claimantAddress
            : this.claimantAddressTyping;
        this.incidentPropertyNo =
          this.claimantPropertyNo > 0 ? this.claimantPropertyNo : 0;
        this.incidentPAE = 2;
      } else {
        this.sameAddress = 0;
        this.clearIncidentAddress();
      }
    },
    incidentPAE: function(val) {
      if (val == 2) {
        this.clearIncidentAddress();
      } else if (val == 1) {
        this.incidentPAE = 1;
        this.clearIncidentAddress();
      } else {
        this.incidentPAE = 0;
        this.clearIncidentAddress();
      }
    },
    personalDOBPicker: function() {
      this.personalDOB = this.saveDateTime(this.personalDOBPicker);
    },
    claimantResident: function() {
      this.clearClaimantAddress();
    }
  },

  computed: {
    fullName: function() {
      return this.claimantFirstName + " " + this.claimantLastName;
    },
    precis: function() {
      let precisVehicle = "";
      if (this.vehicleClaim) {
        precisVehicle = "(Vehicle - " + this.vehicleRegistrationNumber + ") ";
      }
      let precisProperty = "";
      if (this.propertyClaim) {
        precisProperty = "(Property - " + this.propertyDamageDetails + ") ";
      }
      let precisPersonal = "";
      if (this.personalClaim) {
        precisPersonal =
          "(Personal - " + this.fullName + " " + this.personalDOB + ") ";
      }

      return (
        "Insurance Application - " +
        precisVehicle +
        precisProperty +
        precisPersonal
      );
    },
    formData: function() {
      var fields = {};
      fields.incidentDate = this.incidentDate;
      fields.incidentTime = this.incidentTime + " " + this.incidentTimeAMPM;
      fields.incidentAddress = this.incidentAddress;

      fields.incidentAddressPropertyNo =
        this.incidentAddressPropertyNo > 0 ? this.incidentAddressPropertyNo : 0;

      fields.incidentHow = this.incidentHow;
      fields.incidentDamageDetail = this.incidentDamageDetail;
      fields.fileIDs = this.fileIDs;
      fields.incidentWitness = this.incidentWitness;
      fields.incidentWitnesses = this.witnesses;
      fields.incidentPoliceInformed = this.incidentPoliceInformed;
      fields.incidentPoliceReportNumber = this.incidentPoliceReportNumber;
      fields.paeReferenceNumber = this.paeReferenceNumber;
      fields.incidentComments = this.incidentComments;
      fields.vehicleClaim = this.vehicleClaim;
      fields.vehicleRegistrationNumber = this.vehicleRegistrationNumber;
      fields.vehicleMake = this.vehicleMake;
      fields.vehicleModel = this.vehicleModel;
      fields.vehicleInsurer = this.vehicleInsurer;
      fields.vehiclePolicyNumber = this.vehiclePolicyNumber;
      fields.vehicleClaimNumber = this.vehicleClaimNumber;
      fields.propertyClaim = this.propertyClaim;
      fields.propertyDamageDetails = this.propertyDamageDetails;
      fields.propertyInsurer = this.propertyInsurer;
      fields.propertyPolicyNumber = this.propertyPolicyNumber;
      fields.propertyClaimNumber = this.propertyClaimNumber;
      fields.personalClaim = this.personalClaim;
      fields.personalFullName = this.personalFullName;
      fields.personalDOB = this.personalDOB;
      fields.claimantFirstName = this.claimantFirstName;
      fields.claimantLastName = this.claimantLastName;
      fields.claimantEmailAddress = this.claimantEmailAddress;
      fields.claimantContactNumber = this.claimantContactNumber;
      fields.sameAddress = this.sameAddress;
      fields.claimantResident = this.claimantResident;
      fields.claimantAddress =
        this.claimantPropertyNo > 0
          ? this.claimantAddress
          : this.claimantAddressTyping;

      fields.claimantPropertyNo =
        this.claimantPropertyNo > 0 ? this.claimantPropertyNo : 0;

      fields.precis = this.precis;
      fields.subject = "15.53.2.4";

      var actions = [];

      if (this.vehicleClaim) {
        actions.push("A15.53.4.1");
      }
      if (this.personalClaim) {
        actions.push("A15.53.4.2");
      }
      if (this.propertyClaim) {
        actions.push("A15.53.4.3");
      }

      fields.action = actions;

      return JSON.stringify(fields);
    }
  },

  methods: {
    process(fieldName, file, metadata, load, error, progress, abort) {
      console.log("process start");
      console.log(file);
      console.log(metadata);
      console.log(fieldName);
      var self = this;
      try {
        progress(true, 0, 1);

        axios
          .post("/api/filemanagement/process", {
            _token: this.csrf,
            metadata
          })
          .then(response => {
            console.log("promise response: " + response.data.message);
          });

        return {
          abort: () => {
            abort();
            // uploadTask.cancel();
          }
        };
      } catch (e) {
        this.handleError(error, e);
        return {
          abort: () => {
            abort();
          }
        };
      }
    },

    processFile(error, file) {
      // process db.
      console.log("processed " + file.filename);
    },

    handleFilePondInit: function() {},

    // FILE UPLOADS
    setFilenames(value) {
      //alert(value)
      this.files = value1;
      this.filesUploaded = true;
    },
    setDescriptions(value) {
      //alert(value)
      this.descriptions = value;
    },

    // DATE PICKER
    customFormatter(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    saveDate: function(date) {
      return moment(date, "DD/MM/YYYY").format("YYYY/MM/DD");
    },
    saveDateTime: function(date, time) {
      let datetime = moment(date).format("DD/MM/YYYY") + " " + time;
      return moment(datetime, "DD/MM/YYYY hh:mm a").format(
        "YYYY/MM/DD hh:mm a"
      );
    },
    formatDate: function(date) {
      return moment(date, "DD/MM/YYYY").format("LLLL");
    },
    formatDateTime: function(date, time) {
      let datetime = moment(date).format("DD/MM/YYYY") + " " + time;
      return moment(datetime, "DD/MM/YYYY hh:mm a").format("LLLL");
    },
    checkIncidentDateError: function() {
      if (this.$v.incidentDate.$error) {
        this.classIncidentDate = "is-danger";
      }
    },
    checkIncidentDateValue: function() {
      this.$v.incidentDate.$touch();
      this.checkIncidentDateError();
    },
    updateAMPM: function() {
      let arrTime = this.incidentTime.split(":");

      if (parseInt(arrTime[0]) > 12 && parseInt(arrTime[0]) < 24) {
        this.incidentTimeAMPM = "PM";
      } else if (parseInt(arrTime[0]) > 23) {
        let mins = "";
        if (arrTime[1] >= 0) {
          mins = arrTime[1];
        } else {
          mins = "00";
        }
        this.incidentTime = "00:" + mins;
        this.incidentTimeAMPM = "AM";
      } else {
        //this.incidentTimeAMPM = 'AM'
      }
    },

    checkPersonalDOBError: function() {
      if (this.$v.personalDOB.$error) {
        this.classPersonalDOB = "is-danger";
      }
    },
    checkPersonalDOBValue: function() {
      this.$v.personalDOB.$touch();
      this.checkPersonalDOBError();
    },

    // STEP VALIDATION
    validateStep1() {
      // this.$v.formStep1.$touch()
      // var isValid = !this.$v.formStep1.$invalid
      // this.$emit('on-validate', this.$data, isValid)
      // return isValid
      this.$v.formStep1.$touch();
      return !this.$v.formStep1.$invalid;
    },
    validateStep2() {
      this.$v.formStep2.$touch();
      return !this.$v.formStep2.$invalid;
    },
    validateStep3() {
      this.$v.formStep3.$touch();
      return !this.$v.formStep3.$invalid;
    },

    testChange(p, c) {
      if (c == 2) {
        // let timeField = this.$v
        // console.log('set autofocus ' . timeField)
        //this.$refs.timeField.focus()
      }
    },

    validateStep4() {
      this.$v.formStep4.$touch();
      return !this.$v.formStep4.$invalid;
    },
    validateStep5() {
      this.$v.formStep5.$touch();
      return !this.$v.formStep5.$invalid;
    },

    // FILE ATTACHMENT HANDLING
    saveFileIDs(value) {
      this.fileIDs = value;
    },
    appendFileIDs(value) {
      let self = this;
      value.forEach(function(v) {
        if (self.fileIDs.indexOf(v) < 0) {
          self.fileIDs.push(v);
        }
      });
    },
    removeFileID(value) {
      this.fileIDs.splice(this.fileIDs.indexOf(value), 1);
    },

    saveIncidentAddress(value) {
      this.incidentAddress = value;
    },
    saveIncidentPropertyNo(value) {
      this.incidentPropertyNo = value;
    },

    saveClaimantAddress(value) {
      this.claimantAddress = value;
      if (this.sameAddress == true) {
        this.incidentAddress = this.claimantAddress;
      }
    },
    saveClaimantAddressTyping(value) {
      this.claimantAddressTyping = value;
      if (this.sameAddress == true) {
        this.incidentAddress = this.claimantAddressTyping;
      }
    },
    saveClaimantPropertyNo(value) {
      this.claimantPropertyNo = value;

      if (this.sameAddress == true) {
        this.incidentPropertyNo = this.claimantPropertyNo;
      }
    },

    clearClaimantAddress() {
      this.claimantAddress = "";
      this.claimantAddressTyping = "";
      this.claimantPropertyNo = "";
    },
    clearIncidentAddress() {
      this.incidentAddress = "";
      this.incidentPropertyNo = "";
    },

    // onComplete() {
    //   alert('finish')
    //   this.submitForm()
    //   // this.$refs.invisibleRecaptcha.execute()

    //     if(this.enableGoogleCaptch) {
    //       this.$refs.invisibleRecaptcha.execute()
    //     } else {
    //       this.submitForm()
    //     }
    // },

    onSubmit() {
      if (this.validateStep3()) {
        if (this.enableGoogleCaptch) {
          this.$refs.invisibleRecaptcha.execute();
        } else {
          this.submitForm();
        }
      }
    },

    submitForm() {
      this.loading = true;
      axios
        .post("/onlineform/" + this.form, {
          _token: this.csrf,
          formTitle: this.formTitle,
          fullName: this.fullName,
          emailAddress: this.claimantEmailAddress,
          contactNumber: this.claimantContactNumber,
          precis: this.precis,
          formData: this.formData,
          message: ""
        })
        .then(response => {
          console.log(response.data.message);
          this.message = response.data.message;
          this.clear();

          setTimeout(() => (this.submitted = true), 5000);
          this.loading = false;
        });
    },

    onVerify: function(response) {
      if (response) {
        axios
          .post("/api/recaptchaSiteVerify", {
            value: response
          })
          .then(gresponse => {
            console.log(gresponse);

            if (gresponse["data"] === "valid") {
              this.submitForm();
            } else {
              this.loading = false;
              this.message = "Failed verify google captcha!";
              console.log("Failed verify google captcha!");
            }
          })
          .catch(error => {
            console.log(error.response.data);
          });
      } else {
        alert("Failed captcha");
      }
    },
    onExpired: function() {
      console.log("Expired");
    },

    onSuccess(response) {},

    clear() {}
  },

  mounted() {
    this.csrf = window.laravel.csrfToken;
    // set default value
    this.incidentDate = this.saveDateTime(
      this.incidentDatePicker,
      this.incidentTime + " " + this.incidentTimeAMPM
    );
    this.personalDOB = this.saveDate(this.personalDOBPicker);
  }
};
</script>

<style>
#tab_container .container_item {
  display: none;
}

#tab_container .container_item.is-active {
  display: block;
}

.wizard-icon-circle i {
  font-style: normal;
}
.wizard-nav li .stepTitle {
  margin-top: 0.5em;
}
.wizard-nav li .stepTitle.active {
  font-size: 1.3em;
}

#datepicker,
#datepicker2 {
  float: left;
  color: rgba(0, 0, 0, 0);
  border: none;
  box-shadow: none;
  width: 3em;
  background: url("/images/UI/calendar-icon.png");
  background-size: contain;
  background-repeat: no-repeat;
}

#incidentTime {
  width: 4em;
  margin-left: 2em;
  display: inline;
}
</style>


// WEBPACK FOOTER //
// Modules/OnlineForm/Resources/assets/js/components/LayoutInsurance.vue